import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { get, map } from 'lodash'
import { getReq } from '../../utils/request'
import queryKeys from '../queryKeys'
import {
	IJobFirmHeader, IQrScansOriginTableItem, ITrusteeTableItem, JobTableItem
} from '../../reducers/jobs/actions'
import { Organization } from '../../reducers/user/userActions'

export interface SuperOrganizationJobTableItem extends JobTableItem {
	organization: Organization | null
}

export interface ISuperOrganizationJobsParams {
	page?: number
	limit?: number
	search?: string | null
	organizationIDs?: number[]
	users?: number[]
	priceOfferFirmHeaderIDs?: number[]
	withoutFirmHeader?: boolean
	inflationPartAccepted?: boolean | null
	orderBy?: string | null
	orderDirection?: string | null
}

export interface ISuperOrganizationJobsPayload {
	jobs: SuperOrganizationJobTableItem[]
	pagination: {
		page: number
		count: number
		totalCount: number
		monthlyTurnoverTotal: number
	} | null
}

// Poznamka: `mapValues(v => v || undefined)` z reducers/jobs by zahodilo aj `false`,
// takze inflationPartAccepted=false by sa nikdy neodoslalo. Tu zahadzujeme len prazdne hodnoty.
const normalizeParams = (params: ISuperOrganizationJobsParams) => {
	const result: Record<string, any> = {}

	Object.entries(params).forEach(([key, value]) => {
		if (value === null || value === undefined || value === '') {
			return
		}
		if (Array.isArray(value) && value.length === 0) {
			return
		}
		result[key] = value
	})

	return result
}

export const fetchSuperOrganizationJobs = async (params: ISuperOrganizationJobsParams): Promise<ISuperOrganizationJobsPayload> => {
	const { data } = await getReq('/api/admin/super-organization/jobs', normalizeParams(params))

	const originalData: any[] = get(data, 'jobs', [])

	return {
		jobs: map(originalData, (item) => ({
			key: item.id,
			id: item.id,
			note: item.note,
			address: item.address,
			user: item.user,
			organization: item.organization || null,
			firmHeader: (item.firmHeader || null) as IJobFirmHeader | null,
			qrScan: (item?.qrScans?.[0] || null) as IQrScansOriginTableItem,
			trustees: (item.trustees || []) as ITrusteeTableItem[],
			objectTrusteeEmail: item.objectTrusteeEmail,
			ownerEmail: item.ownerEmail,
			createdAt: item.createdAt,
			updatedAt: item.updatedAt
		})) as SuperOrganizationJobTableItem[],
		pagination: get(data, 'context', null)
	}
}

export const useSuperOrganizationJobs = (
	params: ISuperOrganizationJobsParams,
	options?: Omit<UseQueryOptions<ISuperOrganizationJobsPayload>, 'queryKey' | 'queryFn'>
) => useQuery<ISuperOrganizationJobsPayload>({
	queryKey: queryKeys.superOrganization.jobs(params),
	queryFn: () => fetchSuperOrganizationJobs(params),
	keepPreviousData: true,
	...options
})

export default useSuperOrganizationJobs
